import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import CssBaseline from '@mui/material/CssBaseline';
import branding from './config/branding';

// Components
import Login from './components/auth/Login';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Dashboard from './pages/Dashboard';
import EmployeeList from './pages/employees/EmployeeList';
import ProductList from './pages/products/ProductList';
import AttendanceList from './pages/attendance/AttendanceList';
import AttendanceForm from './pages/attendance/AttendanceForm';
import LeaveRequestList from './pages/leave-requests/LeaveRequestList';
import LeaveRequestForm from './pages/leave-requests/LeaveRequestForm';
import PayrollList from './pages/payroll/PayrollList';
import PayrollForm from './pages/payroll/PayrollForm';
import CustomerList from './pages/customers/CustomerList';
import InvoiceList from './pages/invoices/InvoiceList';
import ProjectList from './pages/projects/ProjectList';
import EmployeeForm from './pages/employees/EmployeeForm';
import EmployeeView from './pages/employees/EmployeeView';
import ProductForm from './pages/products/ProductForm';
import ProductView from './pages/products/ProductView';
import CategoryList from './pages/categories/CategoryList';
import CategoryForm from './pages/categories/CategoryForm';
import CategoryView from './pages/categories/CategoryView';
import QuotationList from './pages/quotations/QuotationList';
import QuotationForm from './pages/quotations/QuotationForm';
import QuotationView from './pages/quotations/QuotationView';
import SalesOrderList from './pages/sales-orders/SalesOrderList';
import SalesOrderForm from './pages/sales-orders/SalesOrderForm';
import SalesOrderView from './pages/sales-orders/SalesOrderView';
import PaymentList from './pages/payments/PaymentList';
import PaymentForm from './pages/payments/PaymentForm';
import PaymentView from './pages/payments/PaymentView';
import PurchaseRequisitionList from './pages/purchase-requisitions/PurchaseRequisitionList';
import PurchaseRequisitionForm from './pages/purchase-requisitions/PurchaseRequisitionForm';
import PurchaseOrderList from './pages/purchase-orders/PurchaseOrderList';
import PurchaseOrderForm from './pages/purchase-orders/PurchaseOrderForm';
import SupplierList from './pages/suppliers/SupplierList';
import SupplierForm from './pages/suppliers/SupplierForm';
import AccountList from './pages/accounts/AccountList';
import AccountForm from './pages/accounts/AccountForm';
import JournalEntryList from './pages/journal-entries/JournalEntryList';
import JournalEntryForm from './pages/journal-entries/JournalEntryForm';
import FinancialReports from './pages/financial-reports/FinancialReports';
import CustomerForm from './pages/customers/CustomerForm';
import InvoiceForm from './pages/invoices/InvoiceForm';
import InvoiceView from './pages/invoices/InvoiceView';
import ProjectForm from './pages/projects/ProjectForm';
import Settings from './pages/Settings';
import DepartmentList from './pages/departments/DepartmentList';
import DepartmentForm from './pages/departments/DepartmentForm';
import TaskList from './pages/tasks/TaskList';
import TaskForm from './pages/tasks/TaskForm';
import WarehouseList from './pages/warehouses/WarehouseList';
import WarehouseForm from './pages/warehouses/WarehouseForm';
import StockList from './pages/stock/StockList';
import StockForm from './pages/stock/StockForm';
import TimeEntryList from './pages/time-entries/TimeEntryList';
import TimeEntryForm from './pages/time-entries/TimeEntryForm';
import UserList from './pages/users/UserList';
import UserForm from './pages/users/UserForm';
import RoleList from './pages/roles/RoleList';
import RoleForm from './pages/roles/RoleForm';
import RolePermissions from './pages/roles/RolePermissions';
import ContactList from './pages/contacts/ContactList';
import ContactForm from './pages/contacts/ContactForm';
import ContactGroupList from './pages/contacts/ContactGroupList';
import WhatsAppDashboard from './pages/whatsapp/WhatsAppDashboard';
import WhatsAppChat from './pages/whatsapp/WhatsAppChat';
import WhatsAppReports from './pages/whatsapp/WhatsAppReports';
import WhatsAppSettings from './pages/whatsapp/WhatsAppSettings';
import WhatsAppTest from './pages/whatsapp/WhatsAppTest';
import InstagramDashboard from './pages/instagram/InstagramDashboard';
import ServiceProviderList from './pages/service-providers/ServiceProviderList';
import ServiceProviderSchedule from './pages/service-providers/ServiceProviderSchedule';
import ServiceProviderReport from './pages/service-providers/ServiceProviderReport';
import ServiceProviderOverview from './pages/service-providers/ServiceProviderOverview';
import ServiceProviderLogin from './pages/service-provider-portal/ServiceProviderLogin';
import ServiceProviderDashboard from './pages/service-provider-portal/ServiceProviderDashboard';
import ServiceProviderInvoice from './pages/service-provider-portal/ServiceProviderInvoice';
import ServiceProviderRequestDetail from './pages/service-provider-portal/ServiceProviderRequestDetail';
import RequestList from './pages/requests/RequestList';
import RequestView from './pages/requests/RequestView';
import RequestItemsReport from './pages/requests/RequestItemsReport';
import SystemLogs from './pages/system-logs/SystemLogs';
import ModuleManagement from './pages/modules/ModuleManagement';
import UserModuleAccess from './pages/modules/UserModuleAccess';
import InsuranceCompanies from './pages/InsuranceCompanies';
import InsuranceClients from './pages/InsuranceClients';
import InsuranceClientOverview from './pages/insurance-clients/InsuranceClientOverview';
import InsuranceBroker from './pages/InsuranceBroker';
import ServicePackages from './pages/ServicePackages';
import OperationsOverview from './pages/operations/OperationsOverview';
import AutomationDashboard from './pages/operations/AutomationDashboard';
import OrganizationOverview from './pages/organization/OrganizationOverview';
import SalesOverview from './pages/sales/SalesOverview';
import MarketingOverview from './pages/marketing/MarketingOverview';
import Campaigns from './pages/marketing/Campaigns';
import LinkedIn from './pages/marketing/LinkedIn';
import Twitter from './pages/marketing/Twitter';
import FinanceOverview from './pages/finance/FinanceOverview';
import TaxReport from './pages/finance/TaxReport';
import Reports from './pages/Reports';
import ServiceRequestReport from './pages/reports/ServiceRequestReport';
import Notifications from './pages/Notifications';
import AdminApprovals from './pages/admin/AdminApprovals';
import ApprovalSettings from './pages/settings/ApprovalSettings';
import Profile from './pages/Profile';

// Component to handle redirect from old /whatsapp/chat/:id to new route
const WhatsAppChatRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/operations/whatsapp-chat/${id}`} replace />;
};

const App: React.FC = () => {
  const { i18n } = useTranslation();
  const [darkMode, setDarkMode] = React.useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  // Create theme with white-label colors (no RTL for Arabic)
  const theme = createTheme({
    direction: 'ltr',
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: {
        main: branding.colors.primary,
      },
      secondary: {
        main: branding.colors.secondary,
      },
      success: {
        main: branding.colors.success,
      },
    },
  });

  // Update document title
  React.useEffect(() => {
    document.dir = 'ltr';
    document.title = branding.appName;
  }, [i18n.language]);

  // Listen for dark mode changes (cross-tab via 'storage', same-tab via custom event)
  React.useEffect(() => {
    const syncDarkMode = () => {
      const saved = localStorage.getItem('darkMode');
      setDarkMode(saved ? JSON.parse(saved) : false);
    };
    window.addEventListener('storage', syncDarkMode);
    window.addEventListener('darkModeChanged', syncDarkMode);
    return () => {
      window.removeEventListener('storage', syncDarkMode);
      window.removeEventListener('darkModeChanged', syncDarkMode);
    };
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          
          {/* Service Provider Portal Routes */}
          <Route path="/service-provider/login" element={<ServiceProviderLogin />} />
          <Route
            path="/service-provider/dashboard"
            element={<ServiceProviderDashboard />}
          />
          <Route
            path="/service-provider/invoice/:requestId"
            element={<ServiceProviderInvoice />}
          />
          <Route
            path="/service-provider/request/:id"
            element={<ServiceProviderRequestDetail />}
          />
          
          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employees"
            element={
              <ProtectedRoute>
                <EmployeeList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employees/create"
            element={
              <ProtectedRoute>
                <EmployeeForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employees/:id"
            element={
              <ProtectedRoute>
                <EmployeeView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employees/:id/edit"
            element={
              <ProtectedRoute>
                <EmployeeForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/products"
            element={
              <ProtectedRoute>
                <ProductList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/products/create"
            element={
              <ProtectedRoute>
                <ProductForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/products/:id"
            element={
              <ProtectedRoute>
                <ProductView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/products/:id/edit"
            element={
              <ProtectedRoute>
                <ProductForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/categories"
            element={
              <ProtectedRoute>
                <CategoryList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/categories/create"
            element={
              <ProtectedRoute>
                <CategoryForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/categories/:id"
            element={
              <ProtectedRoute>
                <CategoryView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/categories/:id/edit"
            element={
              <ProtectedRoute>
                <CategoryForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/attendance"
            element={
              <ProtectedRoute>
                <AttendanceList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/attendance/create"
            element={
              <ProtectedRoute>
                <AttendanceForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/attendance/:id/edit"
            element={
              <ProtectedRoute>
                <AttendanceForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leave-requests"
            element={
              <ProtectedRoute>
                <LeaveRequestList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leave-requests/create"
            element={
              <ProtectedRoute>
                <LeaveRequestForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leave-requests/:id/edit"
            element={
              <ProtectedRoute>
                <LeaveRequestForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payroll"
            element={
              <ProtectedRoute>
                <PayrollList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payroll/create"
            element={
              <ProtectedRoute>
                <PayrollForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payroll/:id/edit"
            element={
              <ProtectedRoute>
                <PayrollForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotations"
            element={
              <ProtectedRoute>
                <QuotationList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotations/create"
            element={
              <ProtectedRoute>
                <QuotationForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotations/:id"
            element={
              <ProtectedRoute>
                <QuotationView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotations/:id/edit"
            element={
              <ProtectedRoute>
                <QuotationForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales-orders"
            element={
              <ProtectedRoute>
                <SalesOrderList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales-orders/create"
            element={
              <ProtectedRoute>
                <SalesOrderForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales-orders/:id"
            element={
              <ProtectedRoute>
                <SalesOrderView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales-orders/:id/edit"
            element={
              <ProtectedRoute>
                <SalesOrderForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments"
            element={
              <ProtectedRoute>
                <PaymentList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/create"
            element={
              <ProtectedRoute>
                <PaymentForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/:id"
            element={
              <ProtectedRoute>
                <PaymentView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments/:id/edit"
            element={
              <ProtectedRoute>
                <PaymentForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchase-requisitions"
            element={
              <ProtectedRoute>
                <PurchaseRequisitionList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchase-requisitions/create"
            element={
              <ProtectedRoute>
                <PurchaseRequisitionForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchase-requisitions/:id/edit"
            element={
              <ProtectedRoute>
                <PurchaseRequisitionForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchase-orders"
            element={
              <ProtectedRoute>
                <PurchaseOrderList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchase-orders/create"
            element={
              <ProtectedRoute>
                <PurchaseOrderForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchase-orders/:id/edit"
            element={
              <ProtectedRoute>
                <PurchaseOrderForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/suppliers"
            element={
              <ProtectedRoute>
                <SupplierList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/suppliers/create"
            element={
              <ProtectedRoute>
                <SupplierForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/suppliers/:id/edit"
            element={
              <ProtectedRoute>
                <SupplierForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounts"
            element={
              <ProtectedRoute>
                <AccountList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounts/create"
            element={
              <ProtectedRoute>
                <AccountForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounts/:id/edit"
            element={
              <ProtectedRoute>
                <AccountForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/journal-entries"
            element={
              <ProtectedRoute>
                <JournalEntryList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/journal-entries/create"
            element={
              <ProtectedRoute>
                <JournalEntryForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/journal-entries/:id"
            element={
              <ProtectedRoute>
                <JournalEntryForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/journal-entries/:id/edit"
            element={
              <ProtectedRoute>
                <JournalEntryForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/financial-reports"
            element={
              <ProtectedRoute>
                <FinancialReports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/financial-reports/balance-sheet"
            element={
              <ProtectedRoute>
                <FinancialReports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/financial-reports/income-statement"
            element={
              <ProtectedRoute>
                <FinancialReports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/financial-reports/trial-balance"
            element={
              <ProtectedRoute>
                <FinancialReports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices"
            element={
              <ProtectedRoute>
                <InvoiceList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices/create"
            element={
              <ProtectedRoute>
                <InvoiceForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices/:id"
            element={
              <ProtectedRoute>
                <InvoiceView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices/:id/edit"
            element={
              <ProtectedRoute>
                <InvoiceForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customers"
            element={
              <ProtectedRoute>
                <CustomerList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customers/create"
            element={
              <ProtectedRoute>
                <CustomerForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customers/:id/edit"
            element={
              <ProtectedRoute>
                <CustomerForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects"
            element={
              <ProtectedRoute>
                <ProjectList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/create"
            element={
              <ProtectedRoute>
                <ProjectForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:id/edit"
            element={
              <ProtectedRoute>
                <ProjectForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/departments"
            element={
              <ProtectedRoute>
                <DepartmentList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/departments/create"
            element={
              <ProtectedRoute>
                <DepartmentForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/departments/:id/edit"
            element={
              <ProtectedRoute>
                <DepartmentForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks"
            element={
              <ProtectedRoute>
                <TaskList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks/create"
            element={
              <ProtectedRoute>
                <TaskForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks/:id"
            element={
              <ProtectedRoute>
                <TaskForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/warehouses"
            element={
              <ProtectedRoute>
                <WarehouseList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/warehouses/create"
            element={
              <ProtectedRoute>
                <WarehouseForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/warehouses/:id"
            element={
              <ProtectedRoute>
                <WarehouseForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/stock"
            element={
              <ProtectedRoute>
                <StockList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/stock/create"
            element={
              <ProtectedRoute>
                <StockForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/stock/:id"
            element={
              <ProtectedRoute>
                <StockForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/time-entries"
            element={
              <ProtectedRoute>
                <TimeEntryList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/time-entries/create"
            element={
              <ProtectedRoute>
                <TimeEntryForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/time-entries/:id"
            element={
              <ProtectedRoute>
                <TimeEntryForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <UserList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users/create"
            element={
              <ProtectedRoute>
                <UserForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users/:id"
            element={
              <ProtectedRoute>
                <UserForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/roles"
            element={
              <ProtectedRoute>
                <RoleList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/roles/create"
            element={
              <ProtectedRoute>
                <RoleForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/roles/:id"
            element={
              <ProtectedRoute>
                <RoleForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/roles/:id/permissions"
            element={
              <ProtectedRoute>
                <RolePermissions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contacts"
            element={
              <ProtectedRoute>
                <ContactList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contacts/create"
            element={
              <ProtectedRoute>
                <ContactForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contacts/:id"
            element={
              <ProtectedRoute>
                <ContactForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contact-groups"
            element={
              <ProtectedRoute>
                <ContactGroupList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/instagram"
            element={
              <ProtectedRoute>
                <InstagramDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/service-providers"
            element={
              <ProtectedRoute>
                <ServiceProviderList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/service-providers/schedule"
            element={
              <ProtectedRoute>
                <ServiceProviderSchedule />
              </ProtectedRoute>
            }
          />
          <Route
            path="/service-providers/report"
            element={
              <ProtectedRoute>
                <ServiceProviderReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/service-providers/:id/overview"
            element={
              <ProtectedRoute>
                <ServiceProviderOverview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/insurance-companies"
            element={
              <ProtectedRoute>
                <InsuranceCompanies />
              </ProtectedRoute>
            }
          />
          <Route
            path="/insurance-clients"
            element={
              <ProtectedRoute>
                <InsuranceClients />
              </ProtectedRoute>
            }
          />
          <Route
            path="/insurance-clients/:id/overview"
            element={
              <ProtectedRoute>
                <InsuranceClientOverview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/service-packages"
            element={
              <ProtectedRoute>
                <ServicePackages />
              </ProtectedRoute>
            }
          />
          <Route
            path="/requests"
            element={
              <ProtectedRoute>
                <RequestList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/requests/:id/view"
            element={
              <ProtectedRoute>
                <RequestView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/requests/report"
            element={
              <ProtectedRoute>
                <RequestItemsReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/system-logs"
            element={
              <ProtectedRoute>
                <SystemLogs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules"
            element={
              <ProtectedRoute>
                <ModuleManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/user-access/:moduleId"
            element={
              <ProtectedRoute>
                <UserModuleAccess />
              </ProtectedRoute>
            }
          />
          <Route
            path="/operations/overview"
            element={
              <ProtectedRoute>
                <OperationsOverview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/operations/whatsapp-chat"
            element={
              <ProtectedRoute>
                <WhatsAppChat />
              </ProtectedRoute>
            }
          />
          <Route
            path="/operations/whatsapp-chat/:id"
            element={
              <ProtectedRoute>
                <WhatsAppChat />
              </ProtectedRoute>
            }
          />
          {/* Redirect old /whatsapp route to new /operations/whatsapp-chat */}
          <Route
            path="/whatsapp"
            element={<Navigate to="/operations/whatsapp-chat" replace />}
          />
          <Route
            path="/whatsapp/chat/:id"
            element={<WhatsAppChatRedirect />}
          />
          <Route
            path="/operations/automation"
            element={
              <ProtectedRoute>
                <AutomationDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/organization/overview"
            element={
              <ProtectedRoute>
                <OrganizationOverview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales/overview"
            element={
              <ProtectedRoute>
                <SalesOverview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/marketing/overview"
            element={
              <ProtectedRoute>
                <MarketingOverview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/marketing/campaigns"
            element={
              <ProtectedRoute>
                <Campaigns />
              </ProtectedRoute>
            }
          />
          <Route
            path="/marketing/linkedin"
            element={
              <ProtectedRoute>
                <LinkedIn />
              </ProtectedRoute>
            }
          />
          <Route
            path="/marketing/twitter"
            element={
              <ProtectedRoute>
                <Twitter />
              </ProtectedRoute>
            }
          />
          <Route
            path="/finance/overview"
            element={
              <ProtectedRoute>
                <FinanceOverview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/finance/tax"
            element={
              <ProtectedRoute>
                <TaxReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <Reports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/service-requests"
            element={
              <ProtectedRoute>
                <ServiceRequestReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <Notifications />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-approvals"
            element={
              <ProtectedRoute>
                <AdminApprovals />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/approvals"
            element={
              <ProtectedRoute>
                <ApprovalSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/insurance-broker"
            element={
              <ProtectedRoute>
                <InsuranceBroker />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          
          {/* Redirect root to dashboard */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
};

export default App;